import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useRef, useState } from 'react'

import { useSession } from '@/features/auth/api/useSession'
import {
  CHAT_POLL_MS,
  mergeArrivals,
  useDocumentVisible,
} from '@/features/cases/api/useCaseChat'
import { api } from '@/lib/api'
import { queryKeys } from '@/lib/query'
import { MOCK_CONVERSATIONS, MOCK_THREADS } from '@/mocks/chat'
import { USE_MOCKS } from '@/mocks/fixtures'
import type { ChatMessage, ChatSubjectKind, Conversation } from '@/types/domain'

/* The conversation surface (0029): one thread, on a case OR a quote.
 *
 * A separate module from `useCaseChat` rather than a rewrite of it. The split
 * began as a way to leave a working case panel alone while this file served a
 * page that did not exist yet; since 0051 there is only one panel — `ChatDock`
 * — and it reads from HERE, so `useCaseChat` is no longer a data source at all.
 * What survives in it is the four shared primitives, which is why this module
 * still imports rather than copies them. What this file does NOT do is
 * re-implement the parts that were right: `mergeArrivals` and
 * `useDocumentVisible` are imported, not copied, so a fix to the
 * reconciliation rule lands in both surfaces at once.
 *
 * The single most important behaviour here is the same one AD-22 asks of the
 * panel: the poll is a FORWARD page (`after=<last held id>`), not a refetch of
 * the whole thread. A poll that re-read the conversation would re-send a year
 * of shipment coordination every interval to every open tab.
 */

/** One message as the wire spells it (chat_dto.go MessageDTO).
 *
 *  `edited` and `deleted` are omitempty on the server, so both are optional
 *  here and both mean false when absent. `clientKey` is deliberately never
 *  sent — it identifies a sender's retry, not the message. */
export interface MessageWire {
  id: string
  authorId: string
  authorName: string
  body: string
  sentAt: string
  edited?: boolean
  deleted?: boolean
  /** Case files this message points at (0030). Never bytes — the server sends
   *  the same FileDTO the case's file list uses, object key withheld. */
  attachments?: {
    id: string
    fileName: string
    contentType: string
    byteSize: number
  }[]
  /** Ids of colleagues this message named. The panel highlights by matching
   *  names it already holds from the team. */
  mentions?: string[]
}

interface MessagePageWire {
  items: MessageWire[]
  hasMore: boolean
}

interface ConversationWire {
  id: string
  subjectKind: ChatSubjectKind
  subjectId: string
  archived?: boolean
  lastMessageAt?: string
  unread: number
}

/** Below the server's DefaultPageSize of 50, for useCaseChat's stated reason:
 *  the first open pays for these rows before anything renders. */
const PAGE_SIZE = 30

/** The fallback poll, deliberately SLOWER than the panel's ten seconds.
 *
 *  This surface is meant to be driven by the socket (step 9). The poll behind
 *  it covers three cases the socket cannot: a frame dropped while the buffer
 *  was full, a corporate proxy that refuses the upgrade, and the hub being
 *  process-local — a second replica does not know to wake this reader at all.
 *
 *  Sixty rather than ten because it is a safety net rather than the mechanism.
 *  When the socket lands, this stays; when it has not landed yet, a minute is
 *  the worst-case latency on a surface nobody is watching second by second. */
export const CONVERSATION_POLL_MS = 60_000

export function toChatMessage(wire: MessageWire): ChatMessage {
  return {
    id: wire.id,
    authorId: wire.authorId,
    authorName: wire.authorName,
    body: wire.body,
    sentAt: wire.sentAt,
    edited: wire.edited,
    deleted: wire.deleted,
    attachments: wire.attachments?.map((a) => ({
      id: a.id,
      fileName: a.fileName,
      contentType: a.contentType,
      byteSize: a.byteSize,
    })),
    mentions: wire.mentions,
  }
}

function toConversation(wire: ConversationWire): Conversation {
  return {
    id: wire.id,
    subjectKind: wire.subjectKind,
    subjectId: wire.subjectId,
    archived: wire.archived,
    lastMessageAt: wire.lastMessageAt,
    unread: wire.unread,
  }
}

/** The path a subject's messages live under.
 *
 *  Derived from the kind rather than passed in, because the two routes differ
 *  only in that segment and a caller free to spell it could point a quote's
 *  thread at the case route — where it would be scope-checked against the
 *  wrong module. The server binds the kind to the mount point for the same
 *  reason. */
function messagesPath(kind: ChatSubjectKind, subjectId: string): string {
  return kind === 'quote'
    ? `/quotes/${subjectId}/messages`
    : `/cases/${subjectId}/messages`
}

export interface ConversationThread {
  messages: ChatMessage[]
  isLoading: boolean
  isError: boolean
  hasMore: boolean
  isLoadingMore: boolean
  loadEarlier: () => void
}

/** One thread: its history, its poll, and its page-back.
 *
 *  Mirrors `useCaseChat`'s shape so `ChatPanel` can render either without
 *  knowing which it was handed. */
export function useConversation(
  kind: ChatSubjectKind,
  subjectId: string,
  enabled = true,
  /** How often the forward-page poll runs.
   *
   *  Sixty seconds by default, because this surface is meant to be driven by the
   *  socket and the poll behind it is a safety net for the three cases the
   *  socket cannot cover: a dropped frame, a proxy refusing the upgrade, and a
   *  second replica whose hub is process-local.
   *
   *  The RECORD panels pass CHAT_POLL_MS (ten seconds) instead. That is not a
   *  preference: Story 8.3 states a fifteen-second delivery bound for the case
   *  panel, and moving that surface onto a sixty-second poll would break it on
   *  any deployment where the socket does not connect. */
  pollMs = CONVERSATION_POLL_MS,
): ConversationThread {
  const queryClient = useQueryClient()
  const key = queryKeys.chat.thread(kind, subjectId)
  const visible = useDocumentVisible()

  const [hasMore, setHasMore] = useState(false)
  const [isLoadingMore, setIsLoadingMore] = useState(false)

  const query = useQuery<ChatMessage[]>({
    queryKey: key,
    queryFn: async () => {
      if (USE_MOCKS) return MOCK_THREADS[`${kind}:${subjectId}`] ?? []
      const page = await api.get<MessagePageWire>(messagesPath(kind, subjectId), {
        params: { limit: PAGE_SIZE },
      })
      setHasMore(page.hasMore)
      return page.items.map(toChatMessage)
    },
    enabled: enabled && subjectId !== '',
    /* Zero, not the client's 30s default, for useCaseChat's reason: a stale
       time longer than the poll would make the poll's own refetch a no-op. */
    staleTime: 0,
  })

  /* The poll, as a forward page.
   *
   * Held in a ref so the interval closure does not capture a stale list: the
   * effect must not re-subscribe on every arrival, or the timer resets each
   * time somebody speaks and a busy thread never polls at all. */
  const messagesRef = useRef<ChatMessage[]>([])
  messagesRef.current = query.data ?? []

  useEffect(() => {
    if (!enabled || !visible || USE_MOCKS || subjectId === '') return

    const timer = window.setInterval(() => {
      const held = messagesRef.current
      const last = held[held.length - 1]
      if (!last) return

      void api
        .get<MessagePageWire>(messagesPath(kind, subjectId), {
          params: { limit: PAGE_SIZE, after: last.id },
        })
        .then((page) => {
          if (page.items.length === 0) return
          queryClient.setQueryData<ChatMessage[]>(key, (current) =>
            mergeArrivals(current ?? [], page.items.map(toChatMessage)),
          )
        })
        /* A failed poll is swallowed. The thread on screen is still the last
           good one, and an error banner for a background refresh nobody asked
           for would be noise; the next tick tries again. */
        .catch(() => {})
    }, pollMs)

    return () => window.clearInterval(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, visible, kind, subjectId, pollMs, queryClient])

  const loadEarlier = useCallback(() => {
    const held = messagesRef.current
    const oldest = held[0]
    if (!oldest || isLoadingMore) return

    setIsLoadingMore(true)
    void api
      .get<MessagePageWire>(messagesPath(kind, subjectId), {
        params: { limit: PAGE_SIZE, before: oldest.id },
      })
      .then((page) => {
        setHasMore(page.hasMore)
        queryClient.setQueryData<ChatMessage[]>(key, (current) => [
          ...page.items.map(toChatMessage),
          ...(current ?? []),
        ])
      })
      .catch(() => {})
      .finally(() => setIsLoadingMore(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind, subjectId, isLoadingMore, queryClient])

  return {
    messages: query.data ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
    hasMore,
    isLoadingMore,
    loadEarlier,
  }
}

/** Posting, optimistically (UX-DR-24).
 *
 *  The bubble appears before the server answers, which is why every send
 *  carries a client key: a request that times out on a warehouse connection is
 *  retried, and without the key the retry is a second identical message.
 *
 *  The key is REQUIRED of the caller rather than defaulted here, and minted
 *  once per message at the call site. A default would have to be minted twice —
 *  once for the bubble's id, once for the request — and the two mints would not
 *  agree, so `onSuccess` would remove a bubble that is not the one on screen and
 *  the sender would keep a greyed duplicate until the next full refetch.
 *
 *  The optimistic bubble carries the CLIENT KEY as its id. Not a random
 *  `pending-N`: when the server answers, the real message replaces the bubble by
 *  id, and the poll may ALREADY have delivered the server's copy in the
 *  meantime — so the reconcile has to recognise its own message under both ids.
 *
 *  A refusal rolls the bubble back rather than leaving it greyed forever, and
 *  the composer is handed the text back so nothing typed is lost (UX-DR-18). */
export function useSendMessage(kind: ChatSubjectKind, subjectId: string) {
  const queryClient = useQueryClient()
  const key = queryKeys.chat.thread(kind, subjectId)
  const { data: session } = useSession()

  return useMutation<
    ChatMessage,
    Error,
    { body: string; clientKey: string; fileIds?: string[] },
    { previous: ChatMessage[] | undefined }
  >({
    mutationFn: async ({ body, clientKey, fileIds }) => {
      /* Offline demo: echo the message back instead of posting it, exactly as
         the case panel's send used to. Without this the mock panel can read a
         thread but every send fails and rolls its bubble back, which reads as a
         broken product rather than as an unmocked one. */
      if (USE_MOCKS) {
        return {
          id: clientKey,
          authorId: session?.id ?? '',
          authorName: session?.name ?? '',
          body,
          sentAt: new Date().toISOString(),
        }
      }

      const wire = await api.post<MessageWire>(messagesPath(kind, subjectId), {
        body,
        clientKey,
        /* Ids of files ALREADY uploaded, never bytes. The server verifies each
           one belongs to this conversation's record before linking — a check
           that matters because everyone on the thread gets a download link. */
        fileIds,
      })
      return toChatMessage(wire)
    },

    onMutate: async ({ body, clientKey }) => {
      await queryClient.cancelQueries({ queryKey: key })
      const previous = queryClient.getQueryData<ChatMessage[]>(key)

      const optimistic: ChatMessage = {
        id: clientKey,
        authorId: session?.id ?? '',
        authorName: session?.name ?? '',
        /* The browser's clock, and the one place this file uses it. It is
           replaced by the database's within a round trip; showing "now" beside a
           bubble the sender is looking straight at beats showing nothing. */
        sentAt: new Date().toISOString(),
        body,
        pending: true,
      }

      queryClient.setQueryData<ChatMessage[]>(key, (held) => [
        ...(held ?? []),
        optimistic,
      ])

      return { previous }
    },

    onSuccess: (saved, { clientKey }) => {
      queryClient.setQueryData<ChatMessage[]>(key, (held) => {
        /* Drop the optimistic bubble by its client key, then hand the server's
           copy to mergeArrivals rather than appending it. The poll may already
           have delivered this message TOGETHER WITH a later one from somebody
           else; appending would move it after that newer message, and nothing
           in this file ever re-sorts, so the wrong order would survive until
           the panel remounts. mergeArrivals replaces in place when the id is
           already held and appends only when it is genuinely new. */
        const withoutBubble = (held ?? []).filter(
          (message) => message.id !== clientKey,
        )
        return mergeArrivals(withoutBubble, [saved])
      })
      /* The list's last-message column and unread counts both move when
         anybody speaks, including this reader. */
      void queryClient.invalidateQueries({ queryKey: queryKeys.chat.conversations() })
    },

    onError: (_error, _variables, context) => {
      /* Restore the exact previous thread rather than refetching over it: a
         refetch would also discard any OTHER optimistic bubble in flight, so a
         second send failing would erase the first one that had not landed yet.

         Only when there IS a previous thread. Writing `[]` when nothing was
         captured commits a conversation that had not finished loading as an
         empty one — the panel then says nobody has said anything instead of
         showing its loading state — and it throws away whatever the poll
         landed while the send was in flight. Nothing is stranded by leaving
         the cache alone: the load this send raced is still running, and it
         overwrites the thread, bubble included, when it answers. */
      if (context?.previous) queryClient.setQueryData(key, context.previous)
    },
  })
}

/** Editing a message the reader wrote (0029).
 *
 *  Authorship is the server's gate — there is no capability for it — so a
 *  non-author's attempt answers 403 and surfaces as `.error` here. Every
 *  mutation on this surface needs its error rendered: there is no global
 *  handler, so an unread `.error` is a silent refusal. */
export function useEditMessage(kind: ChatSubjectKind, subjectId: string) {
  const queryClient = useQueryClient()
  const key = queryKeys.chat.thread(kind, subjectId)

  return useMutation<ChatMessage, Error, { id: string; body: string }>({
    mutationFn: async ({ id, body }) => {
      const wire = await api.patch<MessageWire>(`/chat/messages/${id}`, { body })
      return toChatMessage(wire)
    },
    onSuccess: (message) => {
      queryClient.setQueryData<ChatMessage[]>(key, (current) =>
        mergeArrivals(current ?? [], [message]),
      )
    },
  })
}

/** Deleting, which leaves a tombstone rather than a hole.
 *
 *  The server answers 200 WITH the tombstone rather than 204, so the bubble is
 *  replaced in place — merging the returned row is the whole update, and no
 *  refetch is needed. */
export function useDeleteMessage(kind: ChatSubjectKind, subjectId: string) {
  const queryClient = useQueryClient()
  const key = queryKeys.chat.thread(kind, subjectId)

  return useMutation<ChatMessage, Error, string>({
    mutationFn: async (id) => {
      const wire = await api.delete<MessageWire>(`/chat/messages/${id}`)
      return toChatMessage(wire)
    },
    onSuccess: (message) => {
      queryClient.setQueryData<ChatMessage[]>(key, (current) =>
        mergeArrivals(current ?? [], [message]),
      )
      void queryClient.invalidateQueries({ queryKey: queryKeys.chat.conversations() })
    },
  })
}

/** The conversation list, with this reader's unread counts.
 *
 *  One request for every thread the reader may open, and there is no filter to
 *  apply here because the server has already applied it: the composer takes the
 *  records each module's row scope reaches and the chat service then drops the
 *  ones this reader is not running (migration 0051). A record missing from this
 *  list is therefore usually a team question, not a scope one — look at
 *  ChatService.narrowToTeam before looking at casefile.List. */
export function useConversations(enabled = true) {
  return useQuery<Conversation[]>({
    queryKey: queryKeys.chat.conversations(),
    queryFn: async () => {
      if (USE_MOCKS) return MOCK_CONVERSATIONS
      const body = await api.get<{ items: ConversationWire[] }>('/chat/conversations')
      return body.items.map(toConversation)
    },
    enabled,
  })
}

/** Moving this reader's watermark (0029).
 *
 *  Idempotent and monotonic — the server refuses to move a watermark backwards
 *  — so calling it on every open is safe and two tabs cannot make the badge
 *  oscillate. */
export function useMarkRead() {
  const queryClient = useQueryClient()

  return useMutation<void, Error, { conversationId: string; messageId: string }>({
    mutationFn: async ({ conversationId, messageId }) => {
      await api.put<void>(`/chat/conversations/${conversationId}/read`, { messageId })
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.chat.conversations() })
    },
  })
}

export { CHAT_POLL_MS }
